import type { HttpClient } from "../http";
import type {
  VendorDTO,
  VendorSkuConfirmationDTO,
  VendorReturnDTO,
  VendorPayoutDTO,
} from "../types";

export function vendorsResource(http: HttpClient) {
  return {
    register: (body: VendorDTO) => http.post<VendorDTO>("/vendors/register", body),
    list: (status?: string) => {
      const q = status ? `?status=${encodeURIComponent(status)}` : "";
      return http.get<VendorDTO[]>(`/vendors${q}`);
    },
    get: (id: number) => http.get<VendorDTO>(`/vendors/${id}`),
    getByPhone: (phone: string) => http.get<VendorDTO>(`/vendors/by-phone/${encodeURIComponent(phone)}`),
    update: (id: number, body: VendorDTO) => http.put<VendorDTO>(`/vendors/${id}`, body),
    approve: (id: number, reviewerNote?: string, approvedBy?: string) =>
      http.post<VendorDTO>(`/vendors/${id}/approve`, { reviewerNote, approvedBy }),
    reject: (id: number, reviewerNote?: string) =>
      http.post<VendorDTO>(`/vendors/${id}/reject`, { reviewerNote }),
    getConfirmations: (vendorId?: number, fulfillmentDate?: string) => {
      const params = new URLSearchParams();
      if (vendorId) params.set("vendorId", String(vendorId));
      if (fulfillmentDate) params.set("fulfillmentDate", fulfillmentDate);
      const q = params.toString() ? `?${params.toString()}` : "";
      return http.get<VendorSkuConfirmationDTO[]>(`/vendor/confirmations${q}`);
    },
    saveConfirmation: (body: VendorSkuConfirmationDTO) =>
      http.post<VendorSkuConfirmationDTO>("/vendor/confirmations", body),
    bulkSaveConfirmations: (body: VendorSkuConfirmationDTO[]) =>
      http.post<VendorSkuConfirmationDTO[]>("/vendor/confirmations/bulk", body),

    // Returns & QC Inspection Desk
    getReturns: (vendorId?: number, status?: string) => {
      const params = new URLSearchParams();
      if (vendorId) params.set("vendorId", String(vendorId));
      if (status && status !== "ALL") params.set("status", status);
      const q = params.toString() ? `?${params.toString()}` : "";
      return http.get<VendorReturnDTO[]>(`/vendor/returns${q}`);
    },
    getReturn: (id: number) => http.get<VendorReturnDTO>(`/vendor/returns/${id}`),
    createReturn: (body: Partial<VendorReturnDTO>) =>
      http.post<VendorReturnDTO>("/vendor/returns", body),
    markReturnCollected: (id: number, collectedBy?: string, gatePassNumber?: string) =>
      http.post<VendorReturnDTO>(`/vendor/returns/${id}/collect`, { collectedBy, gatePassNumber }),
    disputeReturn: (id: number, disputeReason: string) =>
      http.post<VendorReturnDTO>(`/vendor/returns/${id}/dispute`, { disputeReason }),

    // Payouts & Settlement Ledger (Section 194Q TDS)
    getPayouts: (vendorId?: number) => {
      const params = new URLSearchParams();
      if (vendorId) params.set("vendorId", String(vendorId));
      const q = params.toString() ? `?${params.toString()}` : "";
      return http.get<VendorPayoutDTO[]>(`/vendor/payouts${q}`);
    },
    getPayout: (id: number) => http.get<VendorPayoutDTO>(`/vendor/payouts/${id}`),
    generatePayout: (body: {
      vendorId: number;
      periodStart: string;
      periodEnd: string;
      payoutDate?: string;
    }) => http.post<VendorPayoutDTO>("/vendor/payouts/generate", body),
    processPayout: (id: number, body: { utrNumber?: string; notes?: string }) =>
      http.post<VendorPayoutDTO>(`/vendor/payouts/${id}/process`, body),
  };
}
